import { useCallback, useMemo, useState } from 'react';
import { DEFAULT_ASSAY_FILTER_STATE } from '@/constants/PatientDashboard/patientTimeline';
import type {
  ChartAssay,
  DiseaseBurdenFilters,
  IDiseaseBurdenData,
  InformedMode,
} from '@/types/PatientDashboard/PatientTimeline.types';
import { reconcileDiseaseBurdenFilters } from '@/components/PatientDashboard/TimelineTab/Timeline/DiseaseBurden.util';

interface IDiseaseBurdenFiltersState {
  filters: DiseaseBurdenFilters;
  hiddenAnalytes: Record<string, boolean>;
  toggleAssay: (analyte: string, assay: ChartAssay) => void;
  setAssayMode: (analyte: string, assay: ChartAssay, mode: InformedMode) => void;
  toggleVisibility: (analyte: string) => void;
}

export function useDiseaseBurdenFilters(
  data: IDiseaseBurdenData,
): IDiseaseBurdenFiltersState {
  const [overrides, setOverrides] = useState<DiseaseBurdenFilters>({});
  const [hiddenAnalytes, setHiddenAnalytes] = useState<Record<string, boolean>>({});

  const filters = useMemo(
    () => reconcileDiseaseBurdenFilters(data, overrides),
    [data, overrides],
  );

  const toggleAssay = useCallback((analyte: string, assay: ChartAssay): void => {
    setOverrides((previous) => {
      const current = previous[analyte]?.[assay] ?? DEFAULT_ASSAY_FILTER_STATE;
      return {
        ...previous,
        [analyte]: {
          ...previous[analyte],
          [assay]: { ...current, isSelected: !current.isSelected },
        },
      };
    });
  }, []);

  const toggleVisibility = useCallback((analyte: string): void => {
    setHiddenAnalytes((previous) => ({ ...previous, [analyte]: !previous[analyte] }));
  }, []);

  const setAssayMode = useCallback((
    analyte: string,
    assay: ChartAssay,
    mode: InformedMode,
  ): void => {
    setOverrides((previous) => {
      const current = previous[analyte]?.[assay] ?? DEFAULT_ASSAY_FILTER_STATE;
      return {
        ...previous,
        [analyte]: {
          ...previous[analyte],
          [assay]: { ...current, mode },
        },
      };
    });
  }, []);

  return {
    filters, hiddenAnalytes, toggleAssay, setAssayMode, toggleVisibility,
  };
}
